import { breadcrumbScript } from "@/lib/seo-jsonld";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookmarkPlus, SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { azError } from "@/lib/az-errors";
import {
  createSavedSearch,
  deleteSavedSearch,
  describeSearch,
  fetchSavedSearches,
  type SavedSearchParams,
} from "@/lib/saved-searches";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AdCard } from "@/components/ad-card";
import { ads, categories, cities } from "@/lib/data";
import { fetchActiveListings } from "@/lib/listings";
import { brandFilterFor, matchesBrand } from "@/lib/specs";
import type { Ad } from "@/lib/data";

type SearchParams = {
  q?: string;
  kateqoriya?: string;
  seher?: string;
  sirala?: string;
  marka?: string;
  model?: string;
  min?: number | undefined;
  max?: number | undefined;
};

export const Route = createFileRoute("/elanlar")({
  // SEO: boş filtrlər URL-ə yazılmır — /elanlar təmiz və kanonik qalır
  validateSearch: (s: Record<string, unknown>): SearchParams => {
    const str = (k: string) => {
      const v = s[k];
      return typeof v === "string" && v.trim() ? v : undefined;
    };
    const out: SearchParams = {};
    for (const k of ["q", "kateqoriya", "seher", "marka", "model"] as const) {
      const v = str(k);
      if (v) out[k] = v;
    }
    const sirala = str("sirala");
    if (sirala && sirala !== "yeni") out.sirala = sirala;
    if (s["min"]) out.min = Number(s["min"]);
    if (s["max"]) out.max = Number(s["max"]);
    return out;
  },
  head: ({ match }) => ({
    meta: [
      // Filtrli/axtarış variantları indekslənmir — əsas versiya /elanlar
      ...(Object.keys(match.search ?? {}).length > 0
        ? [{ name: "robots", content: "noindex, follow" }]
        : [{ name: "robots", content: "index, follow" }]),
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Elan axtarışı — Albazar.az" },
      {
        name: "description",
        content:
          "Kateqoriya, şəhər və qiymətə görə elanları filtrləyin. Yeni, köhnə, ucuz və baha sıralama.",
      },
      { property: "og:title", content: "Elan axtarışı — Albazar.az" },
      { property: "og:description", content: "Kateqoriya, şəhər və qiymət filtri ilə elan axtarışı." },
    
      { property: "og:url", content: "https://albazar.az/elanlar" },
    ],
    links: [{ rel: "canonical", href: "https://albazar.az/elanlar" }],
    scripts: [breadcrumbScript([{ name: "Elanlar", path: "/elanlar" }])],
  }),
  component: Listing,
});

export function useAllAds(): Ad[] {
  const { data } = useQuery({
    queryKey: ["listings", "active"],
    queryFn: fetchActiveListings,
    staleTime: 30_000,
  });
  return useMemo(() => [...(data ?? []), ...ads], [data]);
}

export function useFiltered(params: SearchParams, source?: Ad[]) {
  const all = source ?? ads;
  return useMemo(() => {
    let list = all.filter((a) => a.status === "active");
    if (params.q)
      list = list.filter((a) => a.title.toLowerCase().includes(params.q!.toLowerCase()));
    if (params.kateqoriya) list = list.filter((a) => a.category === params.kateqoriya);
    if (params.marka)
      list = list.filter((a) => matchesBrand(a, params.marka!, params.model || undefined));
    if (params.seher) list = list.filter((a) => a.city === params.seher);
    if (params.min) list = list.filter((a) => a.price >= params.min!);
    if (params.max) list = list.filter((a) => a.price <= params.max!);
    const sorted = [...list];
    switch (params.sirala) {
      case "kohne":
        sorted.sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt));
        break;
      case "ucuz":
        sorted.sort((a, b) => a.price - b.price);
        break;
      case "baha":
        sorted.sort((a, b) => b.price - a.price);
        break;
      default:
        sorted.sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
    }
    return sorted;
  }, [params, all]);
}

function Listing() {
  const raw = Route.useSearch();
  const params = {
    q: raw.q ?? "",
    kateqoriya: raw.kateqoriya ?? "",
    seher: raw.seher ?? "",
    sirala: raw.sirala ?? "yeni",
    marka: raw.marka ?? "",
    model: raw.model ?? "",
    min: raw.min,
    max: raw.max,
  } satisfies SearchParams;
  const navigate = useNavigate({ from: "/elanlar" });
  const all = useAllAds();
  const results = useFiltered(params, all);

  const set = (patch: Partial<SearchParams>) =>
    navigate({ search: (prev: SearchParams) => ({ ...prev, ...patch }) });
  const brandFilter = brandFilterFor(params.kateqoriya);
  const modelOptions = brandFilter?.models[params.marka ?? ""] ?? [];

  return (
    <div className="container-x py-8">
      <h1 className="text-3xl font-extrabold tracking-tight">Elanlar</h1>
      <p className="mt-1 text-sm text-muted-foreground">{results.length} elan tapıldı</p>

      <div className="mt-6 grid gap-3 rounded-3xl border border-border bg-card p-4 shadow-card lg:grid-cols-6">
        <Input
          value={params.q}
          onChange={(e) => set({ q: e.target.value })}
          placeholder="Açar söz"
          aria-label="Açar söz"
          className="h-11 rounded-2xl lg:col-span-2"
        />
        <Select
          value={params.kateqoriya || "hamisi"}
          onValueChange={(v) => set({ kateqoriya: v === "hamisi" ? "" : v, marka: "", model: "" })}
        >
          <SelectTrigger className="h-11! rounded-2xl">
            <SelectValue placeholder="Kateqoriya" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="hamisi">Bütün kateqoriyalar</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.slug} value={c.slug}>
                {c.icon} {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={params.seher || "hamisi"}
          onValueChange={(v) => set({ seher: v === "hamisi" ? "" : v })}
        >
          <SelectTrigger className="h-11! rounded-2xl">
            <SelectValue placeholder="Şəhər" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="hamisi">Bütün şəhərlər</SelectItem>
            {cities.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Input
            type="number"
            value={params.min ?? ""}
            onChange={(e) => set({ min: e.target.value ? Number(e.target.value) : undefined })}
            placeholder="Min ₼"
            aria-label="Minimum qiymət"
            className="h-11 rounded-2xl"
          />
          <Input
            type="number"
            value={params.max ?? ""}
            onChange={(e) => set({ max: e.target.value ? Number(e.target.value) : undefined })}
            placeholder="Max ₼"
            aria-label="Maksimum qiymət"
            className="h-11 rounded-2xl"
          />
        </div>
        <Select value={params.sirala} onValueChange={(v) => set({ sirala: v })}>
          <SelectTrigger className="h-11! rounded-2xl">
            <SlidersHorizontal className="size-4" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yeni">Ən yeni</SelectItem>
            <SelectItem value="kohne">Ən köhnə</SelectItem>
            <SelectItem value="ucuz">Ən ucuz</SelectItem>
            <SelectItem value="baha">Ən baha</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <SavedSearchesBar params={params} onApply={(p) => set({ ...emptyParams, ...p })} />

      {brandFilter && (
        <div className="mt-3 rounded-3xl border border-border bg-card p-4 shadow-card">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Marka
          </p>
          <div className="mt-3 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            <button
              type="button"
              onClick={() => set({ marka: "", model: "" })}
              className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                !params.marka
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border hover:border-primary/50 hover:bg-accent"
              }`}
            >
              Hamısı
            </button>
            {brandFilter.brands.map((b) => (
              <button
                key={b}
                type="button"
                onClick={() => set({ marka: b, model: "" })}
                className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                  params.marka === b
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border hover:border-primary/50 hover:bg-accent"
                }`}
              >
                {b}
              </button>
            ))}
          </div>
          {params.marka && modelOptions.length > 0 && (
            <div className="mt-3 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
              {modelOptions.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => set({ model: params.model === m ? "" : m })}
                  className={`shrink-0 rounded-full border px-3.5 py-1 text-xs font-medium transition-colors ${
                    params.model === m
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border hover:border-primary/50 hover:bg-accent"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {results.length === 0 ? (
        <div className="mt-16 text-center">
          <p className="text-lg font-semibold">Heç bir elan tapılmadı</p>
          <p className="mt-1 text-sm text-muted-foreground">Filtrləri dəyişib yenidən cəhd edin.</p>
          <Button
            className="mt-5 rounded-full"
            onClick={() =>
              set({ q: "", kateqoriya: "", seher: "", marka: "", model: "", min: undefined, max: undefined })
            }
          >
            Filtrləri sıfırla
          </Button>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {results.map((a, i) => (
            <AdCard key={a.id} ad={a} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
const emptyParams: SearchParams = {
  q: "",
  kateqoriya: "",
  seher: "",
  sirala: "yeni",
  marka: "",
  model: "",
  min: undefined,
  max: undefined,
};

function SavedSearchesBar({
  params,
  onApply,
}: {
  params: SearchParams;
  onApply: (p: SavedSearchParams) => void;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const list = useQuery({
    queryKey: ["saved-searches", user?.id],
    queryFn: () => fetchSavedSearches(user!.id),
    enabled: Boolean(user?.id),
  });

  const save = useMutation({
    mutationFn: () => createSavedSearch(user!.id, name || describeSearch(params), params),
    onSuccess: () => {
      setName("");
      toast.success("Axtarış yadda saxlanıldı");
      qc.invalidateQueries({ queryKey: ["saved-searches"] });
    },
    onError: (e) => toast.error(azError(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteSavedSearch(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved-searches"] }),
    onError: (e) => toast.error(azError(e)),
  });

  if (!user) return null;

  return (
    <div className="mt-3 rounded-3xl border border-border bg-card p-4 shadow-card">
      <div className="flex flex-wrap items-center gap-2">
        <BookmarkPlus className="size-4 text-info" />
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Saxlanmış axtarışlar
        </p>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={describeSearch(params)}
          aria-label="Axtarışın adı"
          className="h-10 max-w-64 flex-1 rounded-2xl"
        />
        <Button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="h-10 rounded-2xl font-bold"
        >
          Yadda saxla
        </Button>
      </div>
      {(list.data ?? []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {(list.data ?? []).map((s) => (
            <span
              key={s.id}
              className="flex items-center gap-1 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium"
            >
              <button type="button" onClick={() => onApply(s.params)} className="hover:underline">
                {s.name}
              </button>
              <button
                type="button"
                aria-label="Sil"
                onClick={() => remove.mutate(s.id)}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="size-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
